import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Button } from '@mui/material';
import { needsOnboarding } from '../../hooks/pro/useMyListing';
import { useMyListingSummary } from '../../hooks/pro/useMyListingSummary';

/** What each missing piece is called in French, in the order it is asked for. */
const LABELS = {
  legal_name: 'la raison sociale',
  siret: 'le SIRET',
};

/**
 * Where the listing stands, worked out from what the API answered.
 *
 * Two states are worth a strip across the top of the site, and they are the
 * two where the professional has nothing to see in the directory: a listing
 * they have not finished, and one waiting for a moderator. Anything else —
 * published, or a draft that could be submitted — says enough on the listing's
 * own page.
 */
export function listingBannerState(listing) {
  if (needsOnboarding(listing)) {
    return { kind: 'incomplete', missing: listing?.missing_to_submit ?? [] };
  }

  if ('PENDING' === listing?.status) {
    return { kind: 'pending' };
  }

  return null;
}

/**
 * What a professional needs told about their listing, wherever they are.
 *
 * This is the other half of the decision not to block: the wizard can be left
 * at any point, so something has to keep the invitation visible. A listing
 * nobody finished is invisible to residents, which is a silent failure — the
 * banner is what makes it loud. A listing waiting for a moderator is invisible
 * too, and for a reason the professional can do nothing about, so it says so
 * rather than leaving them to wonder whether they forgot a step.
 *
 * It asks the API rather than deciding for itself what "incomplete" means:
 * `missing_to_submit` is computed by the model.
 */
function ProviderOnboardingBanner() {
  const { pathname } = useLocation();
  const summary = useMyListingSummary();
  // No summary means no professional account, which is nothing to say rather
  // than an unfinished listing.
  const state = null === summary ? null : listingBannerState(summary);

  if (null === state) {
    return null;
  }

  // Never where the page already says it: telling somebody to go where they
  // are is just noise.
  const redundantOn = 'pending' === state.kind ? '/pro/ma-fiche' : '/pro/bienvenue';

  if (pathname.startsWith(redundantOn)) {
    return null;
  }

  // Full width and square: this is a strip across the top of the page, not a
  // card floating in the content.
  const strip = { borderRadius: 0, borderWidth: '0 0 1px 0', px: { xs: 2, md: 5.5 }, alignItems: 'center' };

  if ('pending' === state.kind) {
    return (
      <Alert
        severity="info"
        sx={strip}
        action={
          <Button component={RouterLink} to="/pro/ma-fiche" variant="outlined">
            Voir ma fiche
          </Button>
        }
      >
        <AlertTitle sx={{ mb: 0.25 }}>Votre fiche est en cours de validation</AlertTitle>
        Un modérateur la relit avant qu’elle apparaisse dans l’annuaire. Vous serez prévenu par
        email de la décision.
      </Alert>
    );
  }

  const named = state.missing.map((key) => LABELS[key]).filter(Boolean);

  return (
    <Alert
      severity="warning"
      sx={strip}
      action={
        <Button component={RouterLink} to="/pro/bienvenue" variant="contained">
          Compléter ma fiche
        </Button>
      }
    >
      <AlertTitle sx={{ mb: 0.25 }}>Votre fiche professionnelle n’est pas encore visible</AlertTitle>
      {named.length > 0
        ? `Il manque ${named.join(', ')}.`
        : 'Il reste quelques informations à renseigner.'}
    </Alert>
  );
}

export default ProviderOnboardingBanner;
